/**
 * 🧠 SMART ERROR BOUNDARY - UX Inteligente para Erros
 * Analisa erros e responde de forma contextual e menos disruptiva
 */

import React, { Component, ReactNode } from 'react';
import { notification, Modal, message } from 'antd';
import { ExclamationCircleOutlined, BugOutlined, ReloadOutlined } from '@ant-design/icons';

interface SmartErrorBoundaryProps {
  children: ReactNode;
  level?: 'component' | 'page' | 'app';
  fallbackComponent?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  contextInfo?: Record<string, any>;
}

interface SmartErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorSeverity: 'low' | 'medium' | 'high';
  retryCount: number;
}

class SmartErrorBoundary extends Component<SmartErrorBoundaryProps, SmartErrorBoundaryState> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: SmartErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorSeverity: 'medium',
      retryCount: 0
    };
  }

  /**
   * 🔍 ANÁLISE INTELIGENTE DE ERROS
   */
  private analyzeError(error: Error): 'low' | 'medium' | 'high' {
    const message = error.message.toLowerCase();
    const stack = error.stack && error.stack.toLowerCase() || '';

    // Erros CRÍTICOS (tela completa necessária)
    if (
      message.includes('chunkloaderror') ||
      message.includes('loading chunk') ||
      message.includes('network error') ||
      stack.includes('router') ||
      stack.includes('app.tsx') ||
      message.includes('cannot read properties of undefined')
    ) {
      return 'high';
    }

    // Erros LEVES (apenas toast)
    if (
      message.includes('warning') ||
      message.includes('deprecated') ||
      message.includes('performance') ||
      stack.includes('antd') ||
      message.includes('prop')
    ) {
      return 'low';
    }

    // Erros MÉDIOS (modal)
    return 'medium';
  }

  /**
   * 🎯 RESPOSTA CONTEXTUAL AO ERRO
   */
  private handleErrorResponse(error: Error, severity: 'low' | 'medium' | 'high') {
    const { level = 'component' } = this.props;

    switch (severity) {
      case 'low':
        // Toast discreto para erros leves
        message.warning({
          content: '⚠️ Algo não funcionou como esperado, mas você pode continuar.',
          duration: 4,
          key: 'smart-error'
        });
        break;

      case 'medium':
        // Modal informativo para erros médios
        Modal.warning({
          title: '🐛 Ops! Encontramos um problema',
          content: (
            <div>
              <p>Algo inesperado aconteceu, mas não se preocupe!</p>
              <p><strong>O que você pode fazer:</strong></p>
              <ul>
                <li>Tente a ação novamente</li>
                <li>Recarregue a página se necessário</li>
                <li>Entre em contato se o problema persistir</li>
              </ul>
              {this.state.retryCount < 2 && (
                <p style={{ color: '#1890ff' }}>
                  🔄 Tentaremos recuperar automaticamente em alguns segundos...
                </p>
              )}
            </div>
          ),
          okText: 'Entendi',
          width: 500
        });
        break;

      case 'high':
        // Para erros críticos, deixar o Error Boundary padrão assumir
        // (renderizar tela completa de erro)
        break;
    }
  }

  /**
   * 🛡️ CAPTURA DE ERRO
   */
  static getDerivedStateFromError(error: Error): Partial<SmartErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🧠 [SmartErrorBoundary] Erro capturado:', error, errorInfo);
    
    const severity = this.analyzeError(error);
    
    this.setState({
      error,
      errorInfo,
      errorSeverity: severity,
      retryCount: this.state.retryCount + 1
    });

    // Callback personalizado
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log contextual
    console.error('🔍 [SmartErrorBoundary] Contexto:', {
      level: this.props.level,
      severity,
      retryCount: this.state.retryCount,
      contextInfo: this.props.contextInfo
    });

    // Resposta inteligente
    this.handleErrorResponse(error, severity);

    // Auto-recovery para erros leves e médios
    if (severity !== 'high' && this.state.retryCount < 3) {
      this.scheduleAutoRetry();
    }
  }

  /**
   * 🔄 AUTO-RECUPERAÇÃO
   */
  private scheduleAutoRetry = () => {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    const delay = this.state.errorSeverity === 'low' ? 2000 : 5000;
    
    this.retryTimeout = setTimeout(() => {
      console.log('🔄 [SmartErrorBoundary] Tentando auto-recuperação...');
      this.handleRetry();
    }, delay);
  };

  /**
   * 🎯 RETRY MANUAL
   */
  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
  };

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  /**
   * 🎨 RENDER FALLBACK (apenas para erros críticos)
   */
  private renderFallback() {
    const { error, errorSeverity, retryCount } = this.state;
    const { level = 'component', fallbackComponent } = this.props;

    // Usar fallback customizado se fornecido
    if (fallbackComponent) {
      return fallbackComponent;
    }

    // Para erros não-críticos, não renderizar nada (já tratados via toast/modal)
    if (errorSeverity !== 'high') {
      return this.props.children;
    }

    // Fallback completo apenas para erros críticos
    const isComponentLevel = level === 'component';

    return (
      <div style={{
        padding: isComponentLevel ? '16px' : '32px',
        textAlign: 'center',
        border: isComponentLevel ? '1px dashed #d9d9d9' : 'none',
        borderRadius: isComponentLevel ? '4px' : '0',
        backgroundColor: isComponentLevel ? '#fafafa' : '#f5f5f5',
        minHeight: isComponentLevel ? 'auto' : '300px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <BugOutlined style={{ 
          fontSize: isComponentLevel ? '24px' : '48px', 
          color: '#ff4d4f',
          marginBottom: '16px'
        }} />
        
        <h3 style={{ color: '#262626', marginBottom: '8px' }}>
          {isComponentLevel ? 'Componente com problema' : 'Área indisponível'}
        </h3>
        
        <p style={{ color: '#8c8c8c', marginBottom: '16px' }}>
          {isComponentLevel 
            ? 'Esta parte da página não pôde ser carregada.'
            : 'Esta funcionalidade está temporariamente indisponível.'
          }
        </p>

        {retryCount < 3 && (
          <button
            onClick={this.handleRetry}
            style={{
              backgroundColor: '#1890ff',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <ReloadOutlined /> Tentar Novamente
          </button>
        )}

        {process.env.NODE_ENV === 'development' && error && (
          <details style={{ marginTop: '16px', width: '100%', maxWidth: '500px' }}>
            <summary style={{ cursor: 'pointer', color: '#666' }}>
              Ver detalhes técnicos
            </summary>            
            <pre style={{
              backgroundColor: 'white',
              padding: '12px',
              borderRadius: '4px',
              fontSize: '11px',
              overflow: 'auto',
              maxHeight: '150px',
              textAlign: 'left',
              border: '1px solid #d9d9d9',
              marginTop: '8px'
            }}>
              {error.message}
              {'\n\n'}
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      return this.renderFallback();
    }

    return this.props.children;
  }
}

export default SmartErrorBoundary; 